import React, { useState, useEffect } from 'react';
import { toast, ToastContainer } from 'react-toastify';
import Loader from '../../Components/Loader/Loader';
import { serviceConsumer } from '../../network/ServiceConsumer';

// Oireachtas Fees tab (functional match to design A2 / screens.md §A2).
// Three sections: platform fee %, event fees (toggle + amount), reclaim items.
// Maps to the Oireachtas model (platformFeePercent, fees.*, reclaimItems) and
// the fee engine (helpers/oireachtasFees.js). ECRO-simple: late is percent-only.
const SEED_RECLAIM = [
  { label: 'Program Book Ad (B&W)', amount: 100 },
  { label: 'Full-Page Colour Ad', amount: 300 },
  { label: 'Digital Ad', amount: 100 },
  { label: 'Céilí Ticket', amount: 30 },
];

const FeesFies = (props) => {
  const [loading, setLoading] = useState(false);
  const EventID = localStorage.getItem('ID');

  const [currency, setCurrency] = useState('CAD');
  const [platformPct, setPlatformPct] = useState('6');
  const [family, setFamily] = useState({ enabled: true, amount: '110' });
  const [assessment, setAssessment] = useState({ enabled: true, amount: '30' });
  const [late, setLate] = useState({ enabled: true, value: '25' });
  const [reclaim, setReclaim] = useState(SEED_RECLAIM);

  useEffect(() => {
    (async () => {
      if (!EventID) return;
      try {
        setLoading(true);
        const res = await serviceConsumer('GET', `${process.env.REACT_APP_BASE_URL}/oireachtas/${EventID}`);
        const ev = res.event;
        if (ev) {
          if (ev.currency) setCurrency(ev.currency);
          const f = ev.fees || {};
          const saved = Object.keys(f).length > 0;
          if (ev.platformFeePercent != null) setPlatformPct(String(ev.platformFeePercent));
          setFamily({ enabled: saved ? !!f.familyFee : true, amount: String(f.familyFee ?? 110) });
          setAssessment({ enabled: saved ? !!f.schoolAssessmentPerDancer : true, amount: String(f.schoolAssessmentPerDancer ?? 30) });
          setLate({ enabled: saved ? !!(f.lateFee && f.lateFee.value) : true, value: String(f.lateFee?.value ?? 25) });
          if (ev.reclaimItems?.length) setReclaim(ev.reclaimItems.map((r) => ({ label: r.label, amount: r.amount, key: r.key })));
        }
      } catch (e) { /* new */ } finally { setLoading(false); }
    })();
  }, [EventID]);

  const setReclaimAt = (i, k) => (e) => setReclaim((rs) => rs.map((r, idx) => idx === i ? { ...r, [k]: e.target.value } : r));
  const addReclaim = () => setReclaim((rs) => [...rs, { label: '', amount: '' }]);
  const delReclaim = (i) => setReclaim((rs) => rs.filter((_, idx) => idx !== i));

  const submit = async (e) => {
    e.preventDefault();
    const payload = {
      platformFeePercent: Number(platformPct) || 0,
      fees: {
        familyFee: family.enabled ? Number(family.amount) || 0 : null,
        schoolAssessmentPerDancer: assessment.enabled ? Number(assessment.amount) || 0 : null,
        lateFee: late.enabled ? { mode: 'percent', value: Number(late.value) || 0 } : null,
      },
      reclaimItems: assessment.enabled
        ? reclaim.filter((r) => r.label).map((r, i) => ({ key: r.key || `item_${i}`, label: r.label, amount: Number(r.amount) || 0 }))
        : [],
      page: 3,
    };
    try {
      setLoading(true);
      const res = await serviceConsumer('PUT', `${process.env.REACT_APP_BASE_URL}/oireachtas/${EventID}`, payload);
      if (res.success) {
        localStorage.setItem('TAB', 4);
        if (Number(localStorage.getItem('page')) < 3) localStorage.setItem('page', 3);
        setTimeout(() => props.setValue('4'), 200);
      }
    } catch (err) {
      toast.error(err?.messages || err?.message || 'Failed to save fees. Please try again.');
    } finally { setLoading(false); }
  };

  const lbl = { fontSize: 14, fontWeight: 600 };
  const hint = { fontSize: 12, color: '#797979', marginTop: 2 };
  const amt = { width: 120, padding: '8px 10px' };
  const card = { border: '1px solid #eaeaea', borderRadius: 10, padding: 20, maxWidth: 760, marginBottom: 20 };

  const feeRow = (title, state, setState, unit, help, valueKey = 'amount') => (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16, padding: '12px 0', borderTop: '1px solid #f0f0f0' }}>
      <label style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 200 }}>
        <input type="checkbox" checked={state.enabled} onChange={(e) => setState((s) => ({ ...s, enabled: e.target.checked }))} />
        <span style={lbl}>{title}</span>
      </label>
      {state.enabled && (
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            {unit === '$' && <span>$</span>}
            <input className="dashboard-input" type="number" value={state[valueKey]} onChange={(e) => setState((s) => ({ ...s, [valueKey]: e.target.value }))} style={amt} />
            {unit === '%' && <span>%</span>}
          </div>
          <div style={hint}>{help}</div>
        </div>
      )}
    </div>
  );

  return (
    <div>
      {loading && <Loader />}
      {!loading && (
        <form className="form" autoComplete="off" onSubmit={submit}>
          <div className="detailsHeaders">
            <h4 className="eventPage">Fees</h4>
            <p className="eventDescription">All amounts in {currency}. Every value here is editable.</p>
          </div>

          {/* Section 1 — Platform fee */}
          <div style={{ ...card, background: '#f9fff5', borderColor: '#cfe8bf' }}>
            <div style={{ ...lbl, marginBottom: 6 }}>Platform fee (your commission)</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <input className="dashboard-input" type="number" value={platformPct} onChange={(e) => setPlatformPct(e.target.value)} style={amt} />
              <span>%</span>
            </div>
            <div style={hint}>Platform commission applied at checkout.</div>
          </div>

          {/* Section 2 — Event fees */}
          <div style={card}>
            <div style={{ ...lbl, fontSize: 16 }}>Event fees</div>
            {feeRow('Family Fee', family, setFamily, '$', 'One per family; waived automatically for registered teacher accounts.')}
            {feeRow('School Assessment', assessment, setAssessment, '$', 'Per dancer registered.')}
            {feeRow('Late Entry Surcharge', late, setLate, '%', 'Applied to entries in the late window.', 'value')}
          </div>

          {/* Section 3 — Reclaim items (only when assessment ON) */}
          {assessment.enabled && (
            <div style={card}>
              <div style={{ ...lbl, fontSize: 16 }}>Assessment reclaim items</div>
              <div style={hint}>Available because School Assessment is enabled.</div>
              <div style={{ marginTop: 12 }}>
                {reclaim.map((r, i) => (
                  <div key={i} style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 8 }}>
                    <input className="dashboard-input" placeholder="Item label" value={r.label} onChange={setReclaimAt(i, 'label')} style={{ flex: 1, padding: '8px 10px' }} />
                    <span>$</span>
                    <input className="dashboard-input" type="number" placeholder="0" value={r.amount} onChange={setReclaimAt(i, 'amount')} style={amt} />
                    <button type="button" onClick={() => delReclaim(i)} style={{ border: 'none', background: 'none', color: '#da1414', cursor: 'pointer', fontSize: 18 }}>×</button>
                  </div>
                ))}
                <button type="button" onClick={addReclaim} style={{ border: '1px dashed #588f36', background: '#fff', color: '#588f36', fontWeight: 600, borderRadius: 8, padding: '8px 14px', cursor: 'pointer', marginTop: 4 }}>+ Add item</button>
                <div style={{ ...hint, marginTop: 10 }}>Any assessment a school doesn't allocate becomes a sponsorship.</div>
              </div>
            </div>
          )}

          <div className="mt-2 mb-5" style={{ maxWidth: 760, textAlign: 'right' }}>
            <button className="filled" type="submit">Next</button>
          </div>
        </form>
      )}
      <ToastContainer />
    </div>
  );
};

export default FeesFies;
